import React from 'react';
import { Link } from 'react-router-dom';
import '../CSS/Service.css';
import { ServiceData } from '../Data/services';
import Reveal from './Reveal';
import FaqSection from './FaqSection';

const pad = (n: number) => `0${n}`.slice(-2);

// Dark text on light badge colours, white text on dark ones.
const badgeText = (hex?: string): string => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return '#fff';
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16));
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? '#1b1b1b' : '#fff';
};
const delay = (i: number) => ({ ['--i' as string]: i } as React.CSSProperties);

const ServicePage: React.FC<{ data: ServiceData }> = ({ data }) => {
  return (
    <div className="service-page">
      {/* Banner */}
      <div className="sv_banner" style={{ backgroundImage: `url('${data.banner}')` }}>
        <div className="sv-banner-overlay"></div>
        <div className="sv-banner-content">
          <h1 className="sv-banner-title">{data.name}</h1>
          <p className="sv-banner-subtitle">{data.subtitle}</p>
        </div>
      </div>

      {/* Intro and animated flow */}
      <div className="sv-section sv-intro">
        <Reveal className="sv-container">
          <p className="sv-intro-text">{data.intro}</p>
          <div className="sv-flow" aria-hidden="true" style={{ ['--n' as string]: data.flow.length } as React.CSSProperties}>
            {data.flow.map((label, i) => (
              <React.Fragment key={label}>
                {i > 0 && <span className="sv-line" style={delay(i)}></span>}
                <span className={`sv-node${i === data.flow.length - 1 ? ' sv-node-last' : ''}`} style={delay(i)}>
                  {label}
                </span>
              </React.Fragment>
            ))}
          </div>
        </Reveal>
      </div>

      {/* Capabilities */}
      <div className="sv-section sv-tint">
        <Reveal className="sv-container">
          <h2 className="sv-title">{data.capabilitiesTitle}</h2>
          <div className="sv-grid">
            {data.capabilities.map((cap, i) => (
              <div className="sv-card" key={cap.title} style={delay(i)}>
                <span className="sv-card-number">{pad(i + 1)}</span>
                <h3 className="sv-card-title">{cap.title}</h3>
                <p className="sv-card-text">{cap.text}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>

      {/* Process timeline */}
      {data.process && (
        <div className="sv-section">
          <Reveal className="sv-container">
            <h2 className="sv-title">{data.process.title}</h2>
            <ol className="sv-timeline" style={{ ['--n' as string]: data.process.steps.length } as React.CSSProperties}>
              {data.process.steps.map((step, i) => (
                <li className="sv-step" key={step.title} style={delay(i)}>
                  <span className="sv-step-dot">{i + 1}</span>
                  <h3 className="sv-step-title">{step.title}</h3>
                  <p className="sv-step-text">{step.text}</p>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      )}

      {/* Methods / technologies */}
      {data.methods && (
        <div className="sv-section sv-tint">
          <Reveal className="sv-container">
            <h2 className="sv-title">{data.methods.title}</h2>
            <div className="sv-tiles" style={{ ['--cols' as string]: data.methods.items.length % 4 === 0 ? 4 : 3 } as React.CSSProperties}>
              {data.methods.items.map((item, i) => (
                <div className="sv-tile" key={item.title} style={delay(i)}>
                  <h3 className="sv-tile-title">{item.title}</h3>
                  <p className="sv-tile-text">{item.text}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      )}

      {/* Technology stack */}
      {data.stack && (
        <div className="sv-section">
          <Reveal className="sv-container">
            <h2 className="sv-title">{data.stack.title}</h2>
            <div className="sv-stack">
              {data.stack.groups.map((group, i) => (
                <div className="sv-stack-group" key={group.label} style={delay(i)}>
                  <h3 className="sv-stack-label">{group.label}</h3>
                  {group.items.every((item) => typeof item !== 'string') ? (
                    <ul className="sv-tech-grid">
                      {group.items.map((item) => {
                        const tech = item as { name: string; logo?: string; badge?: string; color?: string };
                        return (
                          <li className="sv-tech" key={tech.name}>
                            {tech.logo ? (
                              <img src={`/Assets/TechLogos/${tech.logo}.svg`} alt="" width="40" height="40" decoding="async" />
                            ) : (
                              <span className="sv-tech-badge" style={{ background: tech.color || '#00437E', color: badgeText(tech.color || '#00437E'), textShadow: 'none' }} aria-hidden="true">
                                {tech.badge || tech.name.slice(0, 2)}
                              </span>
                            )}
                            <span className="sv-tech-name">{tech.name}</span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <ul className="sv-chips">
                      {group.items.map((item) => {
                        const label = typeof item === 'string' ? item : item.name;
                        return <li key={label}>{label}</li>;
                      })}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      )}

      {/* Why Zemantics */}
      {data.why && (
        <div className="sv-section sv-tint">
          <Reveal className="sv-container">
            <h2 className="sv-title">{data.why.title}</h2>
            <div className="sv-grid sv-grid-4">
              {data.why.items.map((item, i) => (
                <div className="sv-card" key={item.title} style={delay(i)}>
                  <h3 className="sv-card-title">{item.title}</h3>
                  <p className="sv-card-text">{item.text}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      )}

      <FaqSection items={data.faq} />

      {/* Call to action */}
      <div className="sv-cta">
        <div className="sv-cta-content">
          <h2 className="sv-cta-title">{data.cta}</h2>
          <Link to="/contact/" className="sv-btn">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ServicePage;
